import {
    authEmailToken,
    ConfigurationContext,
    getEmailTokenPath,
} from '@news-mono/web-common'
import { useContext } from 'react'
import { useQuery } from 'react-query'
import { useDispatch } from 'react-redux'
import { EmailPreferenceSection } from '../components/PreferenceSection'

const PreferenceGroup = [
    'wan_newsletter',
    'wan_promotions',
    'wan_travelclub',
] as const

type TPreferenceGroup = typeof PreferenceGroup[number]

interface Preferences {
    list_ids: string[]
    suppression_ids: number[]
}

export function useGetEmailToken(payload: { enabled: boolean }) {
    const dispatch = useDispatch()
    const { enabled } = payload
    return useQuery<{ token: string }>(
        'fetch-email-token',
        async () => {
            const response = await fetch(getEmailTokenPath)
            if (!response.ok) {
                throw new Error('Failed to fetch email token.')
            }

            const data = (await response.json()) as { token: string }

            dispatch(
                authEmailToken({
                    emailToken: data.token,
                }),
            )

            return data
        },
        {
            refetchOnWindowFocus: false,
            enabled: enabled,
        },
    )
}

export function useEmailPreferenceData(emailToken: string | undefined) {
    const { emailServiceApi } = useContext(ConfigurationContext)
    return useQuery<{ result: Record<TPreferenceGroup, Preferences> }>(
        ['email-preference-data', emailToken],
        async () => {
            if (!emailToken) {
                throw new Error('Unable to get email token')
            }

            const api = `${emailServiceApi}/email-preference-center/email-preferences`

            const response = await fetch(api, {
                headers: {
                    Authorization: emailToken,
                },
            })

            if (!response.ok) {
                throw new Error('Network response was not ok')
            }

            return await response.json()
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!emailToken,
        },
    )
}

export function useGetPreferences(
    preferenceResponse: Record<TPreferenceGroup, Preferences> | undefined,
    preferenceContent: EmailPreferenceSection[][],
): EmailPreferenceSection[][] {
    if (!preferenceResponse) return preferenceContent

    let contactIds: string[] = []
    let suppressionIds: number[] = []

    // Get rid of duplicated ids if there's any
    for (const group in preferenceResponse) {
        contactIds.push(
            ...preferenceResponse[group as TPreferenceGroup].list_ids,
        )
        suppressionIds.push(
            ...preferenceResponse[group as TPreferenceGroup].suppression_ids,
        )
    }
    contactIds = [...new Set(contactIds)]
    suppressionIds = [...new Set(suppressionIds)]

    // Add status to preferences
    preferenceContent.forEach((masthead) => {
        masthead.forEach((section) => {
            let hasSectionItemOptedIn = false
            section.sectionItems.forEach((item) => {
                const isInContacts = item.guid && contactIds.includes(item.guid)
                const isInSuppression =
                    item.unSubId && suppressionIds.includes(item.unSubId)

                item.optIn = !!(isInContacts && !isInSuppression)
                hasSectionItemOptedIn = hasSectionItemOptedIn || item.optIn
            })
            section.hasSectionItemOptedIn = hasSectionItemOptedIn
        })
    })
    return preferenceContent
}
export interface ListIdName {
    listId?: string
    listName: string
}

// Get all lists for anonymous subscribe page
export function useGetAllLists(
    mastheadPreferences: EmailPreferenceSection[][],
): ListIdName[] {
    return mastheadPreferences.reduce(
        (res, masthead) =>
            res.concat([
                ...masthead
                    // Anonymous users will not be able to subscribe subscriber exclusive
                    .filter((section) => !section.subscriberOnly)
                    .reduce(
                        (sectionRes, curSection) =>
                            sectionRes.concat([
                                ...curSection.sectionItems.reduce(
                                    (ItemRes, curItem) =>
                                        ItemRes.concat({
                                            listName: curItem.name,
                                            listId: curItem.id,
                                        }),
                                    [] as ListIdName[],
                                ),
                            ]),
                        [] as ListIdName[],
                    ),
            ]),
        [] as ListIdName[],
    )
}

export function authClickHandler(
    e: React.MouseEvent<HTMLElement>,
    storageKey: string,
    onAuthClick: () => void,
) {
    e.preventDefault()
    sessionStorage.setItem(
        storageKey,
        JSON.stringify({
            useProfileEmail: true,
        }),
    )
    onAuthClick()
}
